// glonet-react-tailwind-project--src--components--hostSplit.js
// The /hosts economic model, shared by the page calculator, the request
// dialog's editable plan card, and the voice agent's set_plan tool — all of
// them must describe the SAME plan from the SAME math.
//
// VALUE ATTRIBUTION — glonet.ai's own ~$20/mo (estimated, not committed) models platform
// value. A host's price splits into two buckets:
//   bucket 1 (first $20): platform value resold -> 50/50, mirroring the
//     Partner affiliate deal (50%; the Standard tier is 25%);
//   bucket 2+ (above $20): the host's brand, with the platform share STEPPING
//     UP at higher prices (owner 2026-07-26 — high-priced nexuses lean harder
//     on the platform): 15% to $100, then 20% to $1,000, then 25% beyond,
//     applied MARGINALLY like tax brackets (no cliffs).
// Under everything: a hard floor of card fees + margin x real costs. Prices
// the floor can't fit get "this price doesn't work" + the minimum — never a
// pretend split. Live values come from public/watercooler/activity; this is
// the offline fallback.
export const SPLIT_FALLBACK = {
  infra_per_member_mo: 0.15, watercooler_per_member_mo: 0.30,
  conv_cost_each: 0.20, conv_per_member_mo: 4.33,
  stripe_pct: 0.029, stripe_fixed: 0.30, margin_multiple: 2.0,
  baseline_price_mo: 20, platform_share_of_base: 0.50,
  platform_share_above_base: 0.15, tier2_at: 100, platform_share_tier2: 0.20,
  tier3_at: 1000, platform_share_tier3: 0.25,
  max_viable_pct: 65, one_time_platform_pct: 30,
};

export function computeSplit(I, { model, price, wc, convos, members }) {
  const p = Math.max(0.5, Number(price) || 0.5);
  const B = I.baseline_price_mo;
  const cost = I.infra_per_member_mo
    + (wc ? I.watercooler_per_member_mo : 0)
    + (convos ? I.conv_cost_each * I.conv_per_member_mo : 0);
  const fees = I.stripe_pct * p + I.stripe_fixed;
  const floor = fees + I.margin_multiple * cost;
  // marginal brackets above the baseline: share steps up at tier2_at/tier3_at
  const t2 = I.tier2_at ?? Infinity, t3 = I.tier3_at ?? Infinity;
  const brandBucket =
    I.platform_share_above_base * Math.max(0, Math.min(p, t2) - B)
    + (I.platform_share_tier2 ?? I.platform_share_above_base) * Math.max(0, Math.min(p, t3) - t2)
    + (I.platform_share_tier3 ?? I.platform_share_above_base) * Math.max(0, p - t3);
  const valueTake = I.platform_share_of_base * Math.min(p, B) + brandBucket;
  const platDollars = Math.max(floor, valueTake);
  const rawPct = (platDollars / p) * 100;
  const viable = model === "one_time" ? true : rawPct <= I.max_viable_pct;
  const platformPct = model === "one_time"
    ? I.one_time_platform_pct
    : (viable ? Math.ceil(rawPct) : null);
  const hostPct = platformPct != null ? 100 - platformPct : null;
  const minPrice = Math.ceil(
    (I.stripe_fixed + I.margin_multiple * cost) / (I.max_viable_pct / 100 - I.stripe_pct));
  return {
    p, B, cost, fees, floor, rawPct, viable, platformPct, hostPct, minPrice,
    floorBinds: viable && model !== "one_time" && floor > valueTake,
    aboveBase: p > B,
    baseBucket: I.platform_share_of_base * Math.min(p, B),
    brandBucket,
    hostMonthly: hostPct != null ? Math.round(members * p * hostPct) / 100 : null,
  };
}
